import React, { useEffect, useMemo } from 'react'
import ReactModal from 'react-modal'
import { Route, Routes, useNavigate } from 'react-router-dom'
import './App.css'
import Navigation from './components/Navigation'
import RequireAuth from './components/RequireAuth'
import TermsOfServiceHandler from './components/TermsOfServiceHandler'
import {
  AnalyticsUserData,
  identifyAnalyticsUser,
  posthog,
} from '@purepm/portals-ui'
import { useUser, useIsAuthorized } from './lib/auth0'
import { setNavigate } from './lib/router'
import routes from './pages/routes'
import FlagRoute from './components/FlagRoute'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { PortalLaunchDarklyFlags } from '@purepm/purepm-lovs'

export default function App() {
  const navigate = useNavigate()

  const { data } = useUser()

  // Set the app element for React Modal
  useEffect(() => {
    ReactModal.setAppElement('#root')
  }, [])

  useEffect(() => {
    if (!data?.dataAccess?.person_id) return

    const brandIds = data.dataAccess.brands
      ? data.dataAccess.brands.map(brand => brand.brand_id).filter(Boolean)
      : undefined

    const analyticsUserData: AnalyticsUserData = {
      person_id: data.dataAccess.person_id,
      roles: data.dataAccess.roles,
      name: data.user.name,
      email: data.user.email,
      brand_ids: brandIds && brandIds.length > 0 ? brandIds : undefined,
      corporation_id: data.dataAccess.corporation_id,
    }

    identifyAnalyticsUser(analyticsUserData)

    if (typeof data.user.email === 'string' && data.user.email.trim() !== '') {
      posthog.register({ email: data.user.email.trim() })
    }
  }, [data])

  const { isAuthorized, isLoading: isAuthLoading } = useIsAuthorized()

  const flags = useFlags()

  const routesNode = useMemo(
    () =>
      routes.map(route => {
        const {
          name,
          path,
          element,
          navigation,
          requireAuth,
          ...otherRouteProps
        } = route
        const navigationVisible = navigation === undefined || !!navigation

        let finalElement = (
          <FlagRoute path={path} isRedirect>
            {element}
          </FlagRoute>
        )

        if (navigationVisible && React.isValidElement(finalElement)) {
          finalElement = <Navigation>{finalElement}</Navigation>
        }

        if (requireAuth) {
          finalElement = <RequireAuth>{finalElement}</RequireAuth>
        }

        return (
          <Route
            key={name}
            path={path}
            element={finalElement}
            {...otherRouteProps}
          />
        )
      }),
    [],
  )

  // Capture navigate to use globally
  useEffect(() => {
    setNavigate(navigate)
  }, [navigate])

  return (
    <>
      {!isAuthLoading &&
        isAuthorized &&
        flags[
          PortalLaunchDarklyFlags.PORTAL_RENTER_WIDGET_TERM_OF_SERVICE_ENABLED
        ] && <TermsOfServiceHandler />}
      <Routes>{routesNode}</Routes>
    </>
  )
}
